import { LocalDate, LocalDateType } from '@app/models';
import { Regex } from '@app/shared/validators/regex';
import { LocalDateTime } from '@util/local-date-time';
import { timeFormatFromInput } from '@util/time';
import { hasNoValue, isNumber } from '@util/type';
import * as dayjs from 'dayjs';
import { Dayjs } from 'dayjs';
import * as arraySupportPlugin from 'dayjs/plugin/arraySupport';
import * as customDateFormatPlugin from 'dayjs/plugin/customParseFormat';
import * as isBetweenPlugin from 'dayjs/plugin/isBetween';
import * as R from 'ramda';

export const FULL_DATE_FORMAT = 'DD.MM.YYYY';
export const FULL_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm:ss';
export const INVALID_DATE  = 'Invalid date';
export const DEFAULT_TIME_FORMAT = 'HH:mm';
export const FULL_TIME_FORMAT = 'HH:mm:ss';

dayjs.extend(arraySupportPlugin);
dayjs.extend(isBetweenPlugin);
dayjs.extend(customDateFormatPlugin);

/**
 * The input from the user must be in the following format:
 * HH{SEPARATOR}MM
 * where SEPARATOR is optional and can be one of the following: ., :, {space}.
 *
 * @param {string} value input value
 * @returns {dayjs} Dayjs object from the input
 */
export const inputTimeToDayjs = (value: string): Dayjs => {
  if (value) {
    const regExp = Regex.timeInputRegex;
    if (regExp.test(value)) {
      const timeArray = regExp.exec(value);
      const hours = timeArray[1];
      const minutes = isNumber(timeArray[2]) ? timeArray[2] : timeArray[3];
      return parseToDayjs([1970, 0, 1, hours, minutes]);
    } else {
      return parseToDayjs(INVALID_DATE);
    }
  }
};

export const toDateTime = (date: Date): LocalDateTime => ({
  dayOfMonth: date.getDate(),
  monthValue: date.getMonth() + 1,
  year: date.getFullYear(),
  hour: date.getHours(),
  minute: date.getMinutes(),
  second: date.getSeconds()
});


export const formatDate = (date?: any, template?: string): string => dayjs(date).format(template);

export const parseToDayjs = (date?: any, template?: string, strict?: boolean): Dayjs => dayjs(date, template, strict);

export const isDayjs = (value: any) => dayjs.isDayjs(value);

const addPrefix = (num) => (`0${num}`).slice(-2);

export const formatDateTime = (dateTime): string => {
  if (dateTime == null) {
    return '';
  }
  const {dayOfMonth, monthValue, year, hour, minute, second} = dateTime;
  return `${addPrefix(dayOfMonth)}-${addPrefix(monthValue)}-${year} ${addPrefix(hour)}:${addPrefix(minute)}:${addPrefix(second)}`;
};

export const toDate = (dateTime: LocalDateTime | Date) => {
  if (dateTime instanceof Date) {
    return dateTime;
  }
  const {dayOfMonth, monthValue, year, hour, minute, second} = <LocalDateTime>dateTime;
  return new Date(year, monthValue - 1, dayOfMonth, hour, minute, second);
};

export const isValidDate = (date: Date) => !isNaN(date.getTime());

/**
 * Converts Date Array to Local Date object.
 *
 * @param {Array} date array - [year, month, day]
 * @return {LocalDate} Local Date object
 * */
export const localDateFromArray = ([year, monthValue, dayOfMonth]: LocalDateType): LocalDate => ({
  year,
  monthValue,
  dayOfMonth
});

/**
 * Converts Date Array to Date object.
 *
 * @param {Array} date array - [year, month, day, hours, minutes, seconds]
 * @return {Date} Date object
 */

export const dateFromArray = (dateArray: LocalDateType): Date => {
  if (hasNoValue(dateArray)) {
    return null;
  }
  const [year, monthValue, dayOfMonth, hours = 0, minutes = 0, seconds = 0] = dateArray;
  return new Date(year, monthValue - 1, dayOfMonth, hours, minutes, seconds);
};

/**
 * Converts LocalDate object to ISO Date string.
 *
 * @param localDate LocalDate object
 * @return {string} ISO date
 * */
export const toISODate = ({year, monthValue, dayOfMonth}: LocalDate) =>
  `${year}-${addPrefix(monthValue)}-${addPrefix(dayOfMonth)}`;

/**
 * Converts Date object to number array.
 * @param date Date
 * @returns {Array<number>} Number array
 */
export const toLocalDateArrayFromDate = (date): LocalDateType => [date.getFullYear(),
  date.getMonth() + 1,
  date.getDate()];

/**
 * Converts Date object to number array.
 * @param date Date
 * @returns {Array<number>} Number array
 */
export const toLocalDateTimeArrayFromDate = (date: Date): LocalDateType => [
  date.getFullYear(),
  date.getMonth() + 1,
  date.getDate(),
  date.getHours(),
  date.getMinutes(),
  date.getSeconds()];

/**
 * Convert local date array to local date time array.
 *
 * @param year year
 * @param monthValue month
 * @param dayOfMonth day
 */
export const toLocalDateTimeArrayFromLocalDateArray = ([year, monthValue, dayOfMonth]: LocalDateType) =>
  [year, monthValue, dayOfMonth, 0, 0, 0];

/**
 * Converts Dayjs object to time number array.
 * @param {Dayjs} value
 * @returns {Array<number>} Number array
 */
export const dayjsToTimeArray = (value: Dayjs): LocalDateType => ([
  1970, 1, 1, value.hour(), value.minute(), value.second()
]);

/**
 * Convert a local date array object into a dayjs.
 *
 * @param localDateArray local date array
 * @returns dayjs
 */
const localDateArrayToDayjs = (localDateArray: LocalDateType) => parseToDayjs([localDateArray[0], localDateArray[1] - 1, localDateArray[2]]);

/**
 * Formats the date array in the given format.
 *
 * @param {number[]} date
 * @returns {string}
 */
export const formatDateArray = (date: number[], format: string): string => formatDate(dateFromArray(date), format);

/**
 * Calculate the difference in days between two dates and checks if it is more than one day.
 *
 * @param date1 first dayjs Date
 * @param date2 second dayjs Date
 * @returns {boolean}
 */
const isDifferenceMoreThanOneDay = (date1: Dayjs, date2: Dayjs) => date2.diff(date1, 'day') > 1;

/**
 * Creates a range object based on the provided indexes.
 *
 * @param days date array
 * @param startIndex start day index
 * @param endIndex end day index
 * @returns {{start: *, end: *}}
 */
const createRange = (days: number[][], startIndex, endIndex) => ({
  start: days[startIndex],
  end: startIndex !== (endIndex - 1) ? days[endIndex - 1] : undefined
});


export const convertToRangeList = (days: number[][]) => {
  const ranges = [];
  if (days && days.length > 0) {
    let rangeStart = localDateArrayToDayjs(days[0]);
    let previous = rangeStart;
    let rangeStartIndex = 0;
    for (let i = 1; i < days.length; i++) {
      const day = days[i];
      const date = localDateArrayToDayjs(day);
      if (isDifferenceMoreThanOneDay(previous, date)) {
        ranges.push(createRange(days, rangeStartIndex, i));
        rangeStart = date;
        rangeStartIndex = i;
      }
      previous = date;
    }
    if (rangeStartIndex <= (days.length - 1)) {
      ranges.push(createRange(days, rangeStartIndex, days.length));
    }
  }
  return ranges;
};

/**
 * Returns tomorrow's date.
 *
 * @return {Date} tomorrow's date.
 */
export const tomorrow = (): LocalDateType => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return toLocalDateArrayFromDate(date);
};

/**
 * Returns today's date.
 *
 * @return {Date} today's date.
 */
export const today = (): LocalDateType => {
  const date = new Date();
  return toLocalDateArrayFromDate(date);
};

/**
 * Check if given date is in the range of the given start and end date. Start and enda date are included too.
 * @param date date to check
 * @param startDate start date
 * @param endDate end date
 */
export const isBetween = (date: LocalDateType, startDate: LocalDateType, endDate: LocalDateType): boolean => {
  const dayjsDate = localDateArrayToDayjs(date);
  return dayjsDate.isBetween(localDateArrayToDayjs(startDate), localDateArrayToDayjs(endDate), 'day', '[]');
};

/**
 * Checks if given date is after some target date.
 * The year, month and day of month are taken into consideration while doing the comparison.
 * @param date date
 * @param targetDate target date
 */
export const isAfter = (date: LocalDateType, targetDate: LocalDateType): boolean =>
  localDateArrayToDayjs(date).isAfter(localDateArrayToDayjs(targetDate));

/**
 * Checks if given date is before some target date.
 * The year, month and day of month are taken into consideration while doing the comparison.
 * @param date date
 * @param targetDate target date
 */
export const isBefore = (date: LocalDateType, targetDate: LocalDateType): boolean =>
  localDateArrayToDayjs(date).isBefore(localDateArrayToDayjs(targetDate));


/**
 * Checks if two dates are equal.
 * The year, month and day of month are taken into consideration while doing the comparison.
 * @param date date
 * @param targetDate target date
 */
export const isSame = (date: LocalDateType, targetDate: LocalDateType): boolean =>
  localDateArrayToDayjs(date).isSame(localDateArrayToDayjs(targetDate));

/**
 * Calculate the difference in days between two dates.
 *
 * @param start start date
 * @param end end date
 * @returns {boolean} difference in days between the two dates
 */
export const differenceInDays = (start: LocalDateType, end: LocalDateType) =>
  localDateArrayToDayjs(end).diff(localDateArrayToDayjs(start), 'day');

export const toDateTimeFromTimeString = (time: string): LocalDateType => {
  if (hasNoValue(time)) {
    return null;
  }

  return dayjsToTimeArray(dayjs(time, timeFormatFromInput(time)));
};

const toTimeArrayFromDateTime = (date: LocalDateType): number[] => {
  if (hasNoValue(date)) {
    return null;
  }
  return R.takeLast(3, date);
};

export const formatTime = (date: LocalDateType): string => {
  const timeTokens = toTimeArrayFromDateTime(date);
  return timeTokens && `${addPrefix(timeTokens[0])}:${addPrefix(timeTokens[1])}:${addPrefix(timeTokens[2])}`;
};
